import { Icon, IconButton, Tooltip, icons } from '@agentisai/design-system';
import { formatArtifactSize, formatArtifactType } from '../lib/formatArtifactMeta';
import { validateUpload } from '../lib/validateUpload';
import { body, card, meta, name as nameCls, row, tile } from './AttachmentChips.css';

/** Longest filename rendered in full. Beyond this the middle is elided. */
const MAX_NAME_CHARS = 24;

/**
 * Elide the MIDDLE of a long filename, never the end.
 *
 * `Q3-consolidated-management-report.pdf` → `Q3-consolid…nt-report.pdf`.
 * Trailing ellipsis (plain `text-overflow`) would eat the extension — the one
 * part that says what the file IS — and two files differing only in their tail
 * (`…-v1.md`, `…-v2.md`) would render identically. The head keeps it
 * recognisable, the tail keeps it distinguishable.
 */
export function elideMiddle(name: string, max = MAX_NAME_CHARS): string {
  if (name.length <= max) return name;
  // Bias toward the tail: the extension plus a few chars of version/date suffix
  // carry more identity than the same number of leading characters.
  const tail = Math.ceil((max - 1) * 0.45);
  const head = max - 1 - tail;
  return `${name.slice(0, head)}…${name.slice(-tail)}`;
}

/** Content type + glyph, from the SAME extension check the upload itself uses. */
function describe(file: File) {
  const validated = validateUpload(file);
  const contentType = validated.ok ? validated.contentType : 'application/octet-stream';
  return {
    contentType,
    glyph: contentType === 'application/pdf' ? icons.FileType : icons.FileText,
  };
}

export interface AttachmentChipsProps {
  /** Files staged for this message, in pick order. Empty renders nothing. */
  readonly files: readonly File[];
  /** Drop a file from the staging list. Nothing has been uploaded yet, so this
   * is a pure client-side removal — no confirm, no request. */
  readonly onRemove: (file: File) => void;
  /** Name of the file currently uploading during a flush, or `null`. */
  readonly uploadingName?: string | null;
  /** 0–100 for {@link uploadingName}'s upload. */
  readonly uploadProgress?: number;
}

/**
 * The staged-attachment row, shared by the create panel and the conversation
 * composer so "attach a file" looks and behaves identically either side of
 * workstream creation.
 *
 * Files are **staged, not uploaded**, until the message is sent (or, pre-create,
 * until the workstream is minted) — which is what makes the X honest: removing a
 * card removes a file nothing has stored yet.
 *
 * Each card reads as a file object rather than a text chip: a type tile, the
 * filename in ink weight, and a muted `type · size` line — the same three parts,
 * in the same order, as the Files rail's `FileCard`, so a file looks like itself
 * wherever it appears.
 */
export function AttachmentChips({
  files,
  onRemove,
  uploadingName = null,
  uploadProgress = 0,
}: AttachmentChipsProps) {
  if (files.length === 0) return null;
  return (
    <div role="group" aria-label="Attached files" className={row}>
      {files.map((file) => {
        const uploading = uploadingName === file.name;
        const { contentType, glyph } = describe(file);
        const display = elideMiddle(file.name);
        const label = `${formatArtifactType(contentType)} · ${formatArtifactSize(file.size)}`;
        return (
          <div
            // Name + size + mtime: a user can legitimately stage two files with
            // the same name from different folders, and React needs them apart.
            key={`${file.name}:${file.size}:${file.lastModified}`}
            className={card}
          >
            <span className={tile} aria-hidden="true">
              <Icon as={glyph} size={16} />
            </span>
            <div className={body}>
              {/* Tooltip only when the name is actually elided — a bubble that
                  repeats what is already fully on screen is noise. The DS
                  Tooltip (portal, focus + hover, aria-describedby) rather than
                  the native `title`, which renders as an OS box in the wrong
                  place and is unreachable by keyboard. */}
              {display === file.name ? (
                <span className={nameCls}>{display}</span>
              ) : (
                <Tooltip content={file.name}>
                  <span className={nameCls}>{display}</span>
                </Tooltip>
              )}
              <span className={meta}>{uploading ? `Uploading… ${uploadProgress}%` : label}</span>
            </div>
            {/* An upload in flight has no cancel path (no AbortController is
                wired), so the card drops its X rather than offering a remove
                that would not actually stop the transfer. The slot stays
                occupied by nothing — the card keeps its width either way. */}
            {!uploading && (
              <IconButton
                // Name the subject: a row of cards all announcing "Remove" tells
                // a screen-reader user nothing about which file they are dropping.
                aria-label={`Remove ${file.name}`}
                size="sm"
                variant="ghost"
                icon={<Icon as={icons.X} size={14} />}
                onClick={() => onRemove(file)}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
AttachmentChips.displayName = 'AttachmentChips';
